import { useState, useEffect } from 'react'
import MobileLayout from '../../components/MobileLayout'
import { employeeApi } from '../../lib/employeeApi'

interface Doc {
  id: string
  name: string
  documentType: string
  fileName: string
  fileSize: number
  createdAt: string
  publishedAt: string | null
  period: string | null
}

const TYPE_LABELS: Record<string, string> = {
  contrat:       'Contrat',
  avenant:       'Avenant',
  bulletin_paie: 'Bulletins de paie',
  fiche_paie:    'Fiche de paie',
  rib:           'RIB',
  identite:      'Identité',
  mutuelle:      'Mutuelle',
  arret_maladie: 'Arrêt maladie',
  diplome:       'Diplôme',
  autre:         'Autre'
}
const TYPE_COLORS: Record<string, string> = {
  contrat:       'bg-blue-100 text-blue-700',
  avenant:       'bg-indigo-100 text-indigo-700',
  bulletin_paie: 'bg-green-100 text-green-700',
  fiche_paie:    'bg-emerald-100 text-emerald-700',
  rib:           'bg-cyan-100 text-cyan-700',
  identite:      'bg-orange-100 text-orange-700',
  mutuelle:      'bg-pink-100 text-pink-700',
  arret_maladie: 'bg-red-100 text-red-700',
  diplome:       'bg-purple-100 text-purple-700',
  autre:         'bg-gray-100 text-gray-600'
}

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

const FILE_ICONS: Record<string, string> = {
  'application/pdf': '📄',
  'image/': '🖼',
  'application/vnd': '📝',
}

function fileIcon(name: string) {
  if (name.endsWith('.pdf')) return '📄'
  if (name.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return '🖼'
  if (name.match(/\.(doc|docx)$/i)) return '📝'
  return '📎'
}

export default function MobileDocuments() {
  const [docs, setDocs] = useState<Doc[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    employeeApi.get('/employee/me/documents')
      .then(r => setDocs(r.data))
      .finally(() => setLoading(false))
  }, [])

  async function download(doc: Doc) {
    try {
      const res = await employeeApi.get(`/employee/me/documents/${doc.id}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.fileName
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      alert('Impossible de télécharger le fichier.')
    }
  }

  // Group by type — exclure les bulletins/fiches de paie (dans l'onglet dédié)
  const PAYSLIP_TYPES = ['bulletin_paie', 'fiche_paie']
  const groups = Object.keys(TYPE_LABELS).map(type => ({
    type,
    docs: docs.filter(d => d.documentType === type && !PAYSLIP_TYPES.includes(d.documentType))
  })).filter(g => g.docs.length > 0)

  return (
    <MobileLayout>
      <div className="p-4 space-y-4">
        <h1 className="text-lg font-bold text-gray-800">Mes documents</h1>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : docs.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <div className="text-4xl mb-3">📂</div>
            <div className="text-gray-500 font-medium">Aucun document</div>
            <div className="text-gray-400 text-sm mt-1">Vos documents apparaîtront ici</div>
          </div>
        ) : (
          groups.map(g => (
            <div key={g.type}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {TYPE_LABELS[g.type]}
              </h2>
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {g.docs.map((doc, i) => (
                  <div key={doc.id} className={`flex items-center gap-3 p-4 ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                    <div className="text-2xl">{fileIcon(doc.fileName)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 text-sm truncate">{doc.name}</div>
                      <div className="text-xs text-gray-400">
                        {fmtSize(doc.fileSize)} · {doc.publishedAt ? fmtDate(doc.publishedAt) : fmtDate(doc.createdAt)}
                        {doc.period && <span className="ml-1 text-blue-500">({doc.period})</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => download(doc)}
                      className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                      title="Télécharger"
                    >
                      ↓
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </MobileLayout>
  )
}
